import React from 'react';
import { motion, useReducedMotion, useTransform } from 'motion/react';
import { cn } from '../../lib/utils';
import { LAST_CHAPTER } from './constants';

const CHAPTER_NAMES = ['Core', 'Salesforce', 'ERP', 'Consultancy', 'AI Agents', 'Worlds'];
const BARS = [0.45, 0.9, 0.6, 0.75];

// Four bars that dance while sound is on
const SoundToggle = ({ on, onToggle }) => {
  const reduced = useReducedMotion();
  return (
    <button
      type="button"
      onClick={() => onToggle()}
      aria-pressed={on}
      className="pointer-events-auto flex items-center gap-3 font-mono text-[0.625rem] uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:text-foreground"
    >
      Sound {on ? 'on' : 'off'}
      <span aria-hidden="true" className="flex h-3.5 items-end gap-[3px]">
        {BARS.map((h, i) => (
          <motion.span
            key={i}
            className="w-[2px] origin-bottom rounded-full bg-current"
            style={{ height: '100%' }}
            animate={on && !reduced ? { scaleY: [h * 0.4, h, h * 0.25, h * 0.8, h * 0.4] } : { scaleY: 0.18 }}
            transition={on && !reduced ? { duration: 1.1 + i * 0.17, repeat: Infinity, ease: 'easeInOut' } : { duration: 0.3 }}
          />
        ))}
      </span>
    </button>
  );
};

/*
 * Instrument layer over the scene: a chapter rail on the left (click to travel), the sound
 * toggle at the bottom right and a progress hairline.
 */
const LabHud = ({ chapter, progress, goTo, sound, onSound, hidden }) => {
  const bar = useTransform(progress, r => r / LAST_CHAPTER);

  return (
    <motion.div className="pointer-events-none absolute inset-0 z-10" animate={{ opacity: hidden ? 0 : 1 }} transition={{ duration: 0.6 }}>
      <nav aria-label="Chapters" className="pointer-events-auto absolute left-[var(--gutter)] top-1/2 hidden -translate-y-1/2 flex-col gap-3 lg:flex">
        {CHAPTER_NAMES.map((name, k) => (
          <button
            key={name}
            type="button"
            onClick={() => goTo(k)}
            aria-current={chapter === k ? 'step' : undefined}
            className={cn(
              'flex items-center gap-3 font-mono text-[0.625rem] uppercase tracking-[0.14em] transition-colors duration-500',
              chapter === k ? 'text-foreground' : 'text-subtle hover:text-muted-foreground'
            )}
          >
            <span className={cn('h-px bg-current transition-[width] duration-500 ease-out-expo', chapter === k ? 'w-8' : 'w-3')} />
            {String(k).padStart(2, '0')} {name}
          </button>
        ))}
      </nav>

      <div className="absolute inset-x-0 bottom-0 flex items-end justify-end px-[var(--gutter)] pb-5">
        <SoundToggle on={sound} onToggle={onSound} />
      </div>
      <div className="absolute inset-x-0 bottom-0 h-px bg-foreground/10">
        <motion.div style={{ scaleX: bar }} className="h-px origin-left bg-gradient-to-r from-brand to-brand-2" />
      </div>
    </motion.div>
  );
};

export default LabHud;
